import { ReactElement, useMemo } from 'react';
import { Checkbox, Typography, ExpandToggle } from '@weareopenr/component-library';
import classnames from 'classnames/bind';
import { getDetailedPublicationStatus } from 'src/helpers/getPublicationStatus/getPublicationStatus';
import type { IPublishDestination, IPublishVenue, IPublishViewProps } from '../../types';
import styles from '../PublishView.module.scss';

const cx = classnames.bind(styles);

interface IChannelPublishItemProps {
  channel: IPublishDestination;
  filteredVenues: IPublishVenue[];
  isExpanded: boolean;
  selectedCount: number;
  watch: IPublishViewProps['watch'];
  register: IPublishViewProps['register'];
  setValue: IPublishViewProps['setValue'];
  onToggleExpand: () => void;
}

const ChannelPublishItem = ({
  channel,
  filteredVenues,
  isExpanded,
  selectedCount,
  watch,
  register,
  setValue,
  onToggleExpand,
}: IChannelPublishItemProps): ReactElement => {
  const watchChannel = watch(`channels.${channel.channel_id}`) ?? {};
  const isChannelSelected = Object.values(watchChannel).includes(true);

  const watchChannelValues = Object.values(watchChannel);
  const isChannelIndeterminate = watchChannelValues.includes(true) && watchChannelValues.includes(false);

  const filteredVenueIds = useMemo(() => new Set(filteredVenues.map((v) => v.venue_id)), [filteredVenues]);
  const handleChannelCheckboxChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    for (const venue of channel.venues) {
      if (filteredVenueIds.has(venue.venue_id)) {
        setValue(`channels.${channel.channel_id}.${venue.venue_id}`, target.checked);
      }
    }
  };

  const expandable = filteredVenues.length ? isExpanded : false;

  return (
    <div className={styles['publish-destination-container']} key={channel.name}>
      <div className={styles['channel-header']}>
        <div className={styles['venue-wrapper']}>
          <Checkbox
            labelWeight="regular"
            indeterminate={isChannelIndeterminate}
            label={channel.name}
            disabled={filteredVenues.length === 0}
            onChange={handleChannelCheckboxChange}
            checked={isChannelSelected}
          >
            <span className={styles['checkbox-parent']}>
              <Typography>{channel.name}</Typography>
              <span className={styles['selected-count']}>Current Selected - {selectedCount}</span>
            </span>
          </Checkbox>
        </div>
        <ExpandToggle
          isExpanded={expandable}
          onClick={filteredVenues.length === 0 ? undefined : onToggleExpand}
          size="medium"
          className={cx('expand-toggle', filteredVenues.length === 0 && 'toggle-disabled')}
        />
      </div>
      {expandable && (
        <div className={styles['venue-container']}>
          {filteredVenues.map((venue) => (
            <div key={venue.venue_id} className={styles['venue-wrapper']}>
              <Checkbox
                labelWeight="regular"
                label={venue.name}
                register={register}
                name={`channels.${channel.channel_id}.${venue.venue_id}`}
              />
              <span className={styles['publication-status']}>
                {venue.publicationDate &&
                  getDetailedPublicationStatus(venue.publicationStatus, venue.publicationDate, venue.priceSchemeName)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChannelPublishItem;
